import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ContactMethods } from 'infrastructure/enums';
import { showResult } from 'infrastructure/hooks/utils';
import { useEffect } from 'react';
import dataService from 'services/dataService';

import { useTableFiltersMap } from './use-console-filter';
import { tableDataQueryKeyMap } from './use-get-table-data';

import type {
  IFollowUpTableData,
  TBaseTableQueryResponse,
} from 'infrastructure/interfaces';

interface IUpdateSmsPreferred {
  patientGuid: string;
  smsPreferred: boolean;
}

const url = 'core-2/ccm/console/preferred-contact-method';
const mutationFn = async ({
  patientGuid,
  smsPreferred,
}: IUpdateSmsPreferred) => {
  const { data, error } = await dataService.updateOnly<{ patientGuid: string }>(
    url,
    {
      patientGuid,
      preferredContactMethod: smsPreferred
        ? ContactMethods.Sms
        : ContactMethods.Email,
    },
  );

  if (error) {
    throw Promise.reject(error);
  }

  return data;
};

export const useUpdateSmsPreferred = () => {
  const queryClient = useQueryClient();
  const tableFiltersMap = useTableFiltersMap();

  const {
    mutate: updateSmsPreferred,
    isLoading,
    error,
  } = useMutation<
    { patientGuid: string } | undefined,
    string | undefined,
    IUpdateSmsPreferred
  >({
    mutationFn,
    onSuccess: (data, variables) => {
      const followUpFilter = tableFiltersMap['follow-up'];
      const followUpQueryKey =
        tableDataQueryKeyMap['follow-up'](followUpFilter);

      queryClient.setQueryData<TBaseTableQueryResponse<IFollowUpTableData>>(
        followUpQueryKey,
        (oldData) => {
          if (!oldData) return oldData;
          const newData = structuredClone(oldData);
          newData.data?.forEach((item) => {
            if (item.guid === variables.patientGuid) {
              item.smsPreferred = variables.smsPreferred;
            }
          });
          return newData;
        },
      );
    },
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { updateSmsPreferred, isLoading };
};
